import { alternateLinks, localePath, SITE_URL, type Locale } from "@/lib/i18n";
import { translate } from "@/lib/i18n-dict";

export type PageSeo = { title: string; description: string };

/**
 * English metadata used when a route has no CMS item or locale-specific SEO
 * record. Keep these strings in the same extraction pipeline as visible copy
 * so a locale can never silently inherit English head metadata.
 */
export const SEO_FALLBACK_STRINGS = [
  "Page unavailable | factory.online",
  "Add-on unavailable | factory.online",
  "Article unavailable | factory.online",
  "Integration unavailable | factory.online",
  "Blog — manufacturing planning insights | factory.online",
  "Practical articles on production planning, scheduling, inventory and MRP for small and mid-sized manufacturers.",
  "Case Studies | factory.online",
  "Customer case studies",
  "See how manufacturers cut lead times, stabilise planning and control cost with factory.online.",
  "Industries | factory.online",
  "How factory.online supports discrete, process and mixed-mode manufacturers across industries.",
  "News | factory.online",
  "Product releases, company updates and manufacturing events from the factory.online team.",
  "Pricing in EUR — factory.online cloud MRP plans",
  "factory.online pricing in EUR: compare Standard, Pro and Enterprise cloud MRP plans, add-ons and per-user costs for your factory.",
] as const;

/**
 * Locale-specific SEO copy for static pages.
 * Ukrainian copy is tuned for local search intent (MRP/ERP для виробництва,
 * планування виробництва, облік складу, собівартість).
 * Locales without an entry fall back to the page's default English copy.
 */
const PAGE_SEO: Partial<Record<Locale, Record<string, PageSeo>>> = {
  uk: {
    "/production": {
      title: "Управління виробництвом — MRP система | factory.online",
      description:
        "Виробничі замовлення, цехові операції, списання матеріалів і статус кожної партії в одній MRP системі. Бачте виконання плану цеху онлайн.",
    },
    "/planning": {
      title: "Планування виробництва MRP — розрахунок потреб | factory.online",
      description:
        "Планування виробництва та закупівель: MRP-розрахунок потреб у матеріалах, дефіцити, терміни постачання і реальні дати відвантаження.",
    },
    "/scheduling": {
      title: "Календарне планування цеху та завантаження обладнання",
      description:
        "Диспетчеризація виробництва: графік операцій, завантаження обладнання та персоналу, вузькі місця і реалістичні строки виконання замовлень.",
    },
    "/purchases": {
      title: "Управління закупівлями на виробництві | factory.online",
      description:
        "Закупівлі під план виробництва: заявки, замовлення постачальникам, ціни, строки поставки та контроль приходу матеріалів на склад.",
    },
    "/stocks": {
      title: "Складський облік на виробництві — залишки онлайн",
      description:
        "Облік складу виробництва: залишки сировини та готової продукції, партії й серійні номери, резерви під замовлення, інвентаризація.",
    },
    "/sales": {
      title: "Замовлення клієнтів і продажі для виробництва",
      description:
        "Управління замовленнями клієнтів: доступність продукції, комерційні пропозиції, підтверджені дати відвантаження та статус кожного замовлення.",
    },
    "/cost": {
      title: "Собівартість продукції — розрахунок і контроль витрат",
      description:
        "Розрахунок собівартості продукції: матеріали, операції, накладні витрати. Порівняння планової та фактичної собівартості за кожним замовленням.",
    },
    "/technical-data": {
      title: "Специфікації BOM і технологічні маршрути виробництва",
      description:
        "Технічні дані виробу: специфікації BOM, техпроцеси й маршрути, версії та зміни конструкції — єдине джерело правди для цеху й планування.",
    },
    "/blog": {
      title: "Блог про виробництво: планування, склад, собівартість",
      description:
        "Практичні статті про планування виробництва, диспетчеризацію, складський облік, закупівлі та MRP для малих і середніх підприємств.",
    },
    "/news": {
      title: "Новини factory.online — оновлення MRP для виробництва",
      description:
        "Новини й оновлення платформи factory.online: нові можливості планування виробництва, складу, закупівель та інтеграцій.",
    },
    "/prices": {
      title: "Ціни на систему для виробництва — тарифи в EUR",
      description:
        "Вартість factory.online для України в EUR: тарифи хмарної MRP, ціна за користувача та доповнення. Порівняйте плани й оберіть свій.",
    },
    "/industries": {
      title: "Галузі виробництва — рішення factory.online",
      description:
        "Рішення для металообробки, кабельного, харчового, хімічного виробництва та позамовного цеху: планування, склад, собівартість під вашу галузь.",
    },
    "/industries/metalworking": {
      title: "Програма для металообробки — прорахунок і завантаження",
      description:
        "Металообробка: швидкий прорахунок замовлень, реальне завантаження верстатів, облік металу й обрізків, контроль собівартості кожної деталі.",
    },
    "/industries/metals-mechanical-engineering": {
      title: "Софт для MilTech і збірного виробництва — Assembly & Miltech",
      description:
        "MilTech і DefTech: розподілені майданчики, контроль версій креслень і прошивок, серійні номери, планова й фактична собівартість виробу.",
    },
    "/industries/cable-wire-manufacturing": {
      title: "Виробництво кабелю і проводу — облік мідi та барабанів",
      description:
        "Кабельне виробництво: рецептури жил та ізоляції, облік міді за LME, довжини й барабани, відходи екструзії та собівартість кожної партії.",
    },
    "/industries/fiber-optic-cables": {
      title: "Виробництво оптоволоконного кабелю — облік партій фібри",
      description:
        "Оптоволоконний кабель: партії фібри без зайвих відходів, забарвлення модулів і послідовність кольорів, протоколи вимірювань по кожному барабану.",
    },
    "/industries/food-and-beverage": {
      title: "Харчове виробництво — рецептури, партії, терміни придатності",
      description:
        "Харчове виробництво: рецептури й перерахунок на партію, простежуваність сировини, терміни придатності та реальна собівартість продукту.",
    },
    "/industries/food-feed-production": {
      title: "Виробництво кормів і харчових сумішей — рецептури й партії",
      description:
        "Корми та харчові суміші: рецептури й дозування, простежуваність партій сировини, контроль якості й собівартість кожного замісу.",
    },
    "/industries/chemical-process-manufacturing": {
      title: "Хімічне виробництво — рецептури, партії, простежуваність",
      description:
        "Хімічне та процесне виробництво: рецептури й виходи, облік партій і залишків, контроль якості та собівартість за кожним замісом.",
    },
    "/industries/aluminum-extrusion": {
      title: "Виробництво алюмінієвого профілю — облік матриць і відходів",
      description:
        "Алюміній та екструзія: планування пресів, облік матриць і сплавів, відходи та повернення, собівартість профілю за кожним замовленням.",
    },
    "/industries/jobshop-custom-production": {
      title: "Позамовне виробництво — прорахунок і контроль строків",
      description:
        "Позамовний цех: швидкі прорахунки під запит клієнта, реальні строки за завантаженням, контроль матеріалів і фактична собівартість замовлення.",
    },
  },
  pl: {
    "/production": {
      title: "Zarządzanie produkcją — system MRP | factory.online",
      description:
        "Zlecenia produkcyjne, operacje na hali, zużycie materiałów i status każdej partii w jednym systemie MRP. Realizacja planu widoczna online.",
    },
    "/planning": {
      title: "Planowanie produkcji MRP — wyliczanie zapotrzebowania",
      description:
        "Planowanie produkcji i zakupów: wyliczanie zapotrzebowania materiałowego, braki, terminy dostaw i realne daty wysyłki zleceń.",
    },
    "/scheduling": {
      title: "Harmonogramowanie produkcji i obciążenie maszyn",
      description:
        "Harmonogram operacji, obciążenie maszyn i ludzi, wąskie gardła oraz realne terminy realizacji zleceń produkcyjnych.",
    },
    "/purchases": {
      title: "Zakupy materiałów pod plan produkcji | factory.online",
      description:
        "Zapotrzebowania, zamówienia do dostawców, ceny i terminy dostaw oraz kontrola przyjęć materiałów na magazyn produkcyjny.",
    },
    "/stocks": {
      title: "Gospodarka magazynowa w produkcji — stany online",
      description:
        "Stany surowców i wyrobów gotowych, partie i numery seryjne, rezerwacje pod zlecenia oraz inwentaryzacja magazynu produkcyjnego.",
    },
    "/sales": {
      title: "Zamówienia klientów i sprzedaż dla produkcji",
      description:
        "Dostępność wyrobów, oferty, potwierdzone daty wysyłki i status każdego zamówienia klienta w jednym systemie dla producenta.",
    },
    "/cost": {
      title: "Koszt wytworzenia — kalkulacja i kontrola kosztów",
      description:
        "Kalkulacja kosztu wyrobu: materiały, operacje i koszty pośrednie. Porównanie kosztu planowanego i rzeczywistego dla każdego zlecenia.",
    },
    "/technical-data": {
      title: "Struktury BOM i marszruty technologiczne produkcji",
      description:
        "Dane techniczne wyrobu: struktury BOM, technologie i marszruty, wersje i zmiany konstrukcyjne — jedno źródło prawdy dla hali i planowania.",
    },
    "/blog": {
      title: "Blog o produkcji: planowanie, magazyn, koszty",
      description:
        "Praktyczne artykuły o planowaniu produkcji, harmonogramowaniu, magazynie, zakupach i MRP dla małych i średnich producentów.",
    },
    "/news": {
      title: "Aktualności factory.online — rozwój systemu MRP",
      description:
        "Nowości i aktualizacje platformy factory.online: nowe możliwości planowania produkcji, magazynu, zakupów i integracji.",
    },
    "/industries": {
      title: "Branże produkcyjne — rozwiązania factory.online",
      description:
        "Rozwiązania dla obróbki metali, produkcji kabli, branży spożywczej i chemicznej oraz produkcji na zamówienie: planowanie, magazyn, koszty.",
    },
    "/prices": {
      title: "Cennik systemu MRP dla produkcji (EUR) | factory.online",
      description:
        "Przejrzysty cennik w EUR: plany Standard, Pro i Enterprise, dodatki i koszt na użytkownika dla małych i średnich zakładów produkcyjnych.",
    },
    "/industries/metalworking": {
      title: "Program dla obróbki metali — planowanie i koszty zleceń",
      description:
        "Obróbka skrawaniem i metalowa: marszruty, obciążenie maszyn CNC, materiały i odpady oraz rzeczywisty koszt każdego zlecenia.",
    },
    "/industries/metals-mechanical-engineering": {
      title: "System dla montażu i produkcji MilTech | factory.online",
      description:
        "Produkcja MilTech: rozproszone lokalizacje, kontrola wersji dokumentacji, numery seryjne i pełna identyfikowalność montażu.",
    },
    "/industries/cable-wire-manufacturing": {
      title: "System dla produkcji kabli i przewodów | factory.online",
      description:
        "Produkcja kablowa: wyciąganie i wytłaczanie, bębny i długości, ceny miedzi, odpady technologiczne i koszt każdej partii kabla.",
    },
    "/industries/fiber-optic-cables": {
      title: "Produkcja kabli światłowodowych — partie i kolory modułów",
      description:
        "Kable światłowodowe: partie włókien i minimalizacja odpadów, barwienie modułów, sekwencje kolorów oraz testy optyczne na bęben.",
    },
    "/industries/food-and-beverage": {
      title: "Program dla produkcji spożywczej — partie i terminy",
      description:
        "Branża spożywcza: receptury, partie i daty przydatności, identyfikowalność surowców, straty produkcyjne i koszt wyrobu.",
    },
    "/industries/food-feed-production": {
      title: "Produkcja pasz i dodatków — receptury i partie",
      description:
        "Produkcja pasz: receptury i mieszanki, kontrola partii i identyfikowalność, planowanie linii oraz koszt każdej wyprodukowanej tony.",
    },
    "/industries/chemical-process-manufacturing": {
      title: "System dla produkcji chemicznej — receptury i procesy",
      description:
        "Produkcja procesowa: receptury i przeliczniki, partie i identyfikowalność, kontrola jakości oraz rzeczywisty koszt wsadu.",
    },
    "/industries/aluminum-extrusion": {
      title: "Wytłaczanie aluminium — planowanie pras i koszt profilu",
      description:
        "Ekstruzja aluminium: planowanie pras, matryce i stopy, złom i zwroty oraz koszt profilu dla każdego zlecenia produkcyjnego.",
    },
    "/industries/jobshop-custom-production": {
      title: "Produkcja na zamówienie — wyceny i kontrola terminów",
      description:
        "Zakład na zamówienie: szybkie wyceny, realne terminy według obciążenia, kontrola materiałów i rzeczywisty koszt każdego zlecenia.",
    },
  },
  de: {
    "/production": {
      title: "Fertigungssteuerung — MRP-System | factory.online",
      description:
        "Fertigungsaufträge, Werkstattoperationen, Materialverbrauch und der Status jeder Charge in einem MRP-System. Planerfüllung jederzeit online sichtbar.",
    },
    "/planning": {
      title: "Produktionsplanung MRP — Materialbedarfsplanung",
      description:
        "Produktions- und Einkaufsplanung: Materialbedarfsrechnung, Fehlteile, Lieferzeiten und realistische Liefertermine für jeden Auftrag.",
    },
    "/scheduling": {
      title: "Feinplanung der Fertigung und Maschinenauslastung",
      description:
        "Werkstattsteuerung: Operationsplan, Auslastung von Maschinen und Personal, Engpässe und realistische Termine für Fertigungsaufträge.",
    },
    "/purchases": {
      title: "Einkauf für die Fertigung — Bestellungen und Termine",
      description:
        "Einkauf nach Produktionsplan: Bedarfe, Lieferantenbestellungen, Preise, Liefertermine und Kontrolle der Wareneingänge im Lager.",
    },
    "/stocks": {
      title: "Lagerverwaltung in der Fertigung — Bestände in Echtzeit",
      description:
        "Bestände an Rohstoffen und Fertigwaren, Chargen und Seriennummern, Reservierungen für Aufträge sowie Inventur im Produktionslager.",
    },
    "/sales": {
      title: "Kundenaufträge und Vertrieb für Fertigungsbetriebe",
      description:
        "Verfügbarkeit der Produkte, Angebote, bestätigte Liefertermine und der Status jedes Kundenauftrags in einem System für Hersteller.",
    },
    "/cost": {
      title: "Herstellkosten — Kalkulation und Kostenkontrolle",
      description:
        "Kalkulation der Herstellkosten: Material, Operationen und Gemeinkosten. Plan- und Ist-Kosten je Fertigungsauftrag im Vergleich.",
    },
    "/technical-data": {
      title: "Stücklisten (BOM) und Arbeitspläne der Fertigung",
      description:
        "Technische Artikeldaten: Stücklisten, Arbeitspläne und Technologien, Versionen und Konstruktionsänderungen als einzige Wahrheitsquelle.",
    },
    "/blog": {
      title: "Blog über Fertigung: Planung, Lager, Herstellkosten",
      description:
        "Praxisnahe Artikel zu Produktionsplanung, Feinplanung, Lagerverwaltung, Einkauf und MRP für kleine und mittlere Fertigungsbetriebe.",
    },
    "/news": {
      title: "News von factory.online — MRP für die Fertigung",
      description:
        "Neuigkeiten und Updates der Plattform factory.online: neue Funktionen für Produktionsplanung, Lager, Einkauf und Integrationen.",
    },
    "/prices": {
      title: "Preise für MRP-Software (EUR) | factory.online",
      description:
        "Transparente Preise in EUR: Tarife Standard, Pro und Enterprise, Add-ons und Kosten pro Nutzer für kleine und mittlere Fertigungsbetriebe.",
    },
    "/industries": {
      title: "Branchen der Fertigung — Lösungen von factory.online",
      description:
        "Lösungen für Metallbearbeitung, Kabelfertigung, Lebensmittel- und Chemieindustrie sowie Auftragsfertigung: Planung, Lager, Kosten.",
    },
    "/industries/metalworking": {
      title: "Software für Metallbearbeitung — Planung und Kosten",
      description:
        "Zerspanung und Metallbau: Arbeitspläne, Auslastung der CNC-Maschinen, Material und Verschnitt sowie die echten Kosten jedes Auftrags.",
    },
    "/industries/metals-mechanical-engineering": {
      title: "Software für Montage und MilTech-Fertigung",
      description:
        "MilTech-Montage: verteilte Standorte, Versionskontrolle von Zeichnungen und Firmware, Seriennummern und lückenlose Rückverfolgbarkeit.",
    },
    "/industries/cable-wire-manufacturing": {
      title: "Software für Kabel- und Drahtfertigung | factory.online",
      description:
        "Kabelfertigung: Ziehen und Extrusion, Trommeln und Längen, Kupferpreise, technologischer Verschnitt und Kosten jeder Kabelcharge.",
    },
    "/industries/fiber-optic-cables": {
      title: "Lichtwellenleiter-Fertigung — Faserchargen und Farbcodes",
      description:
        "LWL-Kabel: Faserchargen und Abfallminimierung, Einfärben der Bündeladern, Farbfolgen und optische Prüfprotokolle je Trommel.",
    },
    "/industries/food-and-beverage": {
      title: "Software für Lebensmittelproduktion — Chargen und MHD",
      description:
        "Lebensmittel: Rezepturen, Chargen und Mindesthaltbarkeit, Rückverfolgbarkeit der Rohstoffe, Produktionsverluste und Herstellkosten.",
    },
    "/industries/food-feed-production": {
      title: "Futtermittelproduktion — Rezepturen und Chargen",
      description:
        "Futtermittel: Rezepturen und Mischungen, Chargenkontrolle und Rückverfolgbarkeit, Linienplanung und Kosten je produzierter Tonne.",
    },
    "/industries/chemical-process-manufacturing": {
      title: "Software für Prozessfertigung — Rezepturen und Chargen",
      description:
        "Chemische Prozessfertigung: Rezepturen und Umrechnungen, Chargen und Rückverfolgbarkeit, Qualitätskontrolle und echte Ansatzkosten.",
    },
    "/industries/aluminum-extrusion": {
      title: "Aluminium-Extrusion — Pressenplanung und Profilkosten",
      description:
        "Extrusion: Planung der Pressen, Matrizen und Legierungen, Schrott und Rückläufe sowie die Kosten des Profils je Fertigungsauftrag.",
    },
    "/industries/jobshop-custom-production": {
      title: "Auftragsfertigung — Kalkulation und Termintreue",
      description:
        "Lohnfertigung: schnelle Angebotskalkulation, realistische Termine nach Auslastung, Materialkontrolle und echte Auftragskosten.",
    },
  },
  fr: {
    "/production": {
      title: "Gestion de production — logiciel MRP | factory.online",
      description:
        "Ordres de fabrication, opérations d'atelier, consommation matière et statut de chaque lot dans un seul MRP. Avancement du plan en temps réel.",
    },
    "/planning": {
      title: "Planification de production MRP — calcul des besoins",
      description:
        "Planification production et achats : calcul des besoins matière, manquants, délais fournisseurs et dates d'expédition réalistes.",
    },
    "/scheduling": {
      title: "Ordonnancement atelier et charge des machines",
      description:
        "Ordonnancement : planning des opérations, charge machines et personnel, goulots d'étranglement et délais réalistes pour vos ordres.",
    },
    "/purchases": {
      title: "Achats pour la production — commandes et délais",
      description:
        "Achats pilotés par le plan : demandes, commandes fournisseurs, prix, délais de livraison et contrôle des réceptions en magasin.",
    },
    "/stocks": {
      title: "Gestion des stocks en production — en temps réel",
      description:
        "Stocks matières et produits finis, lots et numéros de série, réservations par commande et inventaire du magasin de production.",
    },
    "/sales": {
      title: "Commandes clients et ventes pour les industriels",
      description:
        "Disponibilité des produits, devis, dates d'expédition confirmées et statut de chaque commande client dans un seul outil industriel.",
    },
    "/cost": {
      title: "Coût de revient — calcul et contrôle des coûts",
      description:
        "Calcul du coût de revient : matières, opérations et frais généraux. Comparaison du coût prévu et réel pour chaque ordre de fabrication.",
    },
    "/technical-data": {
      title: "Nomenclatures (BOM) et gammes de fabrication",
      description:
        "Données techniques produit : nomenclatures, gammes et procédés, versions et modifications — une seule source de vérité pour l'atelier.",
    },
    "/blog": {
      title: "Blog production : planification, stocks, coût de revient",
      description:
        "Articles pratiques sur la planification de production, l'ordonnancement, les stocks, les achats et le MRP pour PME industrielles.",
    },
    "/news": {
      title: "Actualités factory.online — MRP pour l'industrie",
      description:
        "Nouveautés et mises à jour de la plateforme factory.online : planification, stocks, achats et nouvelles intégrations.",
    },
    "/prices": {
      title: "Tarifs du logiciel MRP (EUR) | factory.online",
      description:
        "Tarifs transparents en EUR : offres Standard, Pro et Enterprise, modules complémentaires et coût par utilisateur pour votre usine.",
    },
    "/industries": {
      title: "Secteurs industriels — solutions factory.online",
      description:
        "Solutions pour la métallurgie, la câblerie, l'agroalimentaire, la chimie et la fabrication à la commande : planification, stocks, coûts.",
    },
    "/industries/metalworking": {
      title: "Logiciel pour la métallurgie — planning et coûts",
      description:
        "Usinage et travail des métaux : gammes, charge des machines CNC, matières et chutes, et coût réel de chaque ordre de fabrication.",
    },
    "/industries/metals-mechanical-engineering": {
      title: "Logiciel pour l'assemblage et la production MilTech",
      description:
        "Assemblage MilTech : sites répartis, gestion des versions des plans et firmwares, numéros de série et traçabilité complète.",
    },
    "/industries/cable-wire-manufacturing": {
      title: "Logiciel pour la fabrication de câbles et fils",
      description:
        "Câblerie : tréfilage et extrusion, tourets et longueurs, cours du cuivre, chutes technologiques et coût de chaque lot de câble.",
    },
    "/industries/fiber-optic-cables": {
      title: "Câbles à fibre optique — lots de fibre et couleurs",
      description:
        "Fibre optique : lots de fibre et réduction des chutes, coloration des modules, séquences de couleurs et tests optiques par touret.",
    },
    "/industries/food-and-beverage": {
      title: "Logiciel agroalimentaire — lots et DLC",
      description:
        "Agroalimentaire : recettes, lots et dates de péremption, traçabilité des matières, pertes de production et coût de revient produit.",
    },
    "/industries/food-feed-production": {
      title: "Production d'aliments pour animaux — recettes et lots",
      description:
        "Alimentation animale : formules et mélanges, contrôle des lots et traçabilité, planning des lignes et coût par tonne produite.",
    },
    "/industries/chemical-process-manufacturing": {
      title: "Logiciel pour la chimie de procédé — recettes et lots",
      description:
        "Fabrication en procédé : recettes et conversions, lots et traçabilité, contrôle qualité et coût réel de chaque charge produite.",
    },
    "/industries/aluminum-extrusion": {
      title: "Extrusion aluminium — planning presses et coût profilé",
      description:
        "Extrusion : planning des presses, filières et alliages, rebuts et retours, et coût du profilé pour chaque ordre de fabrication.",
    },
    "/industries/jobshop-custom-production": {
      title: "Fabrication à la commande — chiffrage et délais",
      description:
        "Atelier à la commande : chiffrages rapides, délais réalistes selon la charge, suivi matière et coût réel de chaque affaire.",
    },
  },
  it: {
    "/production": {
      title: "Gestione della produzione — sistema MRP | factory.online",
      description:
        "Ordini di produzione, operazioni di reparto, consumi di materiale e stato di ogni lotto in un unico MRP. Avanzamento del piano in tempo reale.",
    },
    "/planning": {
      title: "Pianificazione della produzione MRP — calcolo fabbisogni",
      description:
        "Pianificazione di produzione e acquisti: calcolo dei fabbisogni materiali, mancanti, tempi di fornitura e date di spedizione realistiche.",
    },
    "/scheduling": {
      title: "Schedulazione di reparto e carico macchine",
      description:
        "Schedulazione: piano delle operazioni, carico di macchine e persone, colli di bottiglia e tempi realistici per gli ordini di produzione.",
    },
    "/purchases": {
      title: "Acquisti per la produzione — ordini e tempi di consegna",
      description:
        "Acquisti guidati dal piano: richieste, ordini ai fornitori, prezzi, tempi di consegna e controllo degli arrivi a magazzino.",
    },
    "/stocks": {
      title: "Gestione magazzino in produzione — giacenze in tempo reale",
      description:
        "Giacenze di materie prime e prodotti finiti, lotti e matricole, impegni per commessa e inventario del magazzino di produzione.",
    },
    "/sales": {
      title: "Ordini clienti e vendite per le aziende manifatturiere",
      description:
        "Disponibilità dei prodotti, offerte, date di spedizione confermate e stato di ogni ordine cliente in un unico sistema per la fabbrica.",
    },
    "/cost": {
      title: "Costo di produzione — calcolo e controllo dei costi",
      description:
        "Calcolo del costo di prodotto: materiali, operazioni e costi indiretti. Confronto tra costo previsto e consuntivo per ogni commessa.",
    },
    "/technical-data": {
      title: "Distinte base (BOM) e cicli di lavorazione",
      description:
        "Dati tecnici di prodotto: distinte base, cicli e tecnologie, versioni e modifiche tecniche — un'unica fonte di verità per il reparto.",
    },
    "/blog": {
      title: "Blog sulla produzione: pianificazione, magazzino, costi",
      description:
        "Articoli pratici su pianificazione della produzione, schedulazione, magazzino, acquisti e MRP per le PMI manifatturiere.",
    },
    "/news": {
      title: "Novità factory.online — MRP per la produzione",
      description:
        "Aggiornamenti della piattaforma factory.online: nuove funzioni per pianificazione, magazzino, acquisti e integrazioni.",
    },
    "/prices": {
      title: "Prezzi del software MRP (EUR) | factory.online",
      description:
        "Prezzi trasparenti in EUR: piani Standard, Pro ed Enterprise, moduli aggiuntivi e costo per utente per PMI manifatturiere.",
    },
    "/industries": {
      title: "Settori manifatturieri — soluzioni factory.online",
      description:
        "Soluzioni per metalmeccanica, produzione di cavi, alimentare e chimica e produzione su commessa: pianificazione, magazzino, costi.",
    },
    "/industries/metalworking": {
      title: "Software per la lavorazione dei metalli — piani e costi",
      description:
        "Lavorazioni meccaniche: cicli, carico delle macchine CNC, materiali e sfridi e costo reale di ogni ordine di produzione.",
    },
    "/industries/metals-mechanical-engineering": {
      title: "Software per assemblaggio e produzione MilTech",
      description:
        "Assemblaggio MilTech: siti distribuiti, controllo delle revisioni di disegni e firmware, numeri di serie e tracciabilità completa.",
    },
    "/industries/cable-wire-manufacturing": {
      title: "Software per la produzione di cavi e fili",
      description:
        "Produzione cavi: trafilatura ed estrusione, bobine e metrature, prezzo del rame, sfridi tecnologici e costo di ogni lotto di cavo.",
    },
    "/industries/fiber-optic-cables": {
      title: "Cavi in fibra ottica — lotti di fibra e codici colore",
      description:
        "Fibra ottica: lotti di fibra e riduzione degli sfridi, colorazione dei moduli, sequenze cromatiche e test ottici per ogni bobina.",
    },
    "/industries/food-and-beverage": {
      title: "Software per l'industria alimentare — lotti e scadenze",
      description:
        "Alimentare: ricette, lotti e scadenze, tracciabilità delle materie prime, perdite di produzione e costo del prodotto finito.",
    },
    "/industries/food-feed-production": {
      title: "Produzione di mangimi — ricette e lotti",
      description:
        "Mangimi: formule e miscele, controllo dei lotti e tracciabilità, pianificazione delle linee e costo per tonnellata prodotta.",
    },
    "/industries/chemical-process-manufacturing": {
      title: "Software per la chimica di processo — ricette e lotti",
      description:
        "Produzione di processo: ricette e conversioni, lotti e tracciabilità, controllo qualità e costo reale di ogni batch prodotto.",
    },
    "/industries/aluminum-extrusion": {
      title: "Estrusione alluminio — piano presse e costo profilo",
      description:
        "Estrusione: pianificazione delle presse, matrici e leghe, scarti e resi e costo del profilo per ogni ordine di produzione.",
    },
    "/industries/jobshop-custom-production": {
      title: "Produzione su commessa — preventivi e rispetto dei tempi",
      description:
        "Officina su commessa: preventivi rapidi, date realistiche in base al carico, controllo materiali e costo reale di ogni commessa.",
    },
  },
};

export function pageSeo(path: string, locale: Locale, fallback: PageSeo): PageSeo {
  const source = PAGE_SEO[locale]?.[path] ?? fallback;
  return {
    title: translate(locale, source.title),
    description: translate(locale, source.description),
  };
}

export function pageUrl(path: string, locale: Locale): string {
  return `${SITE_URL}${localePath(locale, path)}`;
}

/** Feature (module) pages that share the same share image. */
const FEATURE_PATHS = new Set([
  "/sales",
  "/production",
  "/purchases",
  "/stocks",
  "/planning",
  "/scheduling",
  "/cost",
  "/technical-data",
]);

/** 1200x630 share image for the page, or undefined when the section has none. */
export function pageImage(path: string): string | undefined {
  if (path === "/prices") return `${SITE_URL}/og/og-prices.jpg`;
  if (path === "/industries" || path.startsWith("/industries/"))
    return `${SITE_URL}/og/og-industries.jpg`;
  if (FEATURE_PATHS.has(path)) return `${SITE_URL}/og/og-features.jpg`;
  return undefined;
}

/** Meta + links for a static page, localised where translations exist. */
export function pageHead(path: string, locale: Locale, fallback: PageSeo) {
  const seo = pageSeo(path, locale, fallback);
  const url = pageUrl(path, locale);
  const image = pageImage(path);
  return {
    meta: [
      { title: seo.title },
      { name: "description", content: seo.description },
      { property: "og:title", content: seo.title },
      { property: "og:description", content: seo.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:locale", content: locale },
      { name: "twitter:card", content: "summary_large_image" },
      ...(image
        ? [
            { property: "og:image", content: image },
            { property: "og:image:width", content: "1200" },
            { property: "og:image:height", content: "630" },
            { name: "twitter:image", content: image },
          ]
        : []),
    ],
    links: [...alternateLinks(path), { rel: "canonical", href: url }],
  };
}
